async function request(path, opts = {}) {
  const isForm = opts.body instanceof FormData
  const res = await fetch(path, {
    // FormData must set its own content-type — it carries the multipart boundary.
    headers: { ...(isForm ? {} : { 'content-type': 'application/json' }), ...(opts.headers || {}) },
    ...opts,
    body: opts.body && typeof opts.body !== 'string' && !isForm
      ? JSON.stringify(opts.body)
      : opts.body,
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`)
  return data
}

export const api = {
  products: () => request('/api/products'),
  product: (sku) => request(`/api/products/${encodeURIComponent(sku)}`),
  // variations for a handful of products at once — the finish pages price a whole
  // table of items without asking for each product separately
  variations: (skus) => request(`/api/variations?skus=${encodeURIComponent(skus.join(','))}`),
  settings: () => request('/api/settings'),
  createOrder: (payload, authToken) =>
    request('/api/orders', {
      method: 'POST',
      body: payload,
      headers: authToken ? { 'x-auth-token': authToken } : {},
    }),
  order: (id, token) => request(`/api/orders/${id}?t=${encodeURIComponent(token)}`),
  // receipt is optional — when present the report goes up as multipart with the slip.
  reportPayment: (id, token, receipt) => {
    if (!receipt) return request(`/api/orders/${id}/pay`, { method: 'POST', body: { token } })
    const form = new FormData()
    form.set('token', token)
    form.set('receipt', receipt)
    return request(`/api/orders/${id}/pay`, { method: 'POST', body: form })
  },
  stripeSession: (id, token) => request(`/api/orders/${id}/stripe`, { method: 'POST', body: { token } }),
}

// Downscale + re-encode photos in the browser before upload. Source files up to 100MB
// are accepted — they always leave the browser as a web-sized JPEG under the server limit.
const MAX_SOURCE = 100 * 1024 * 1024
const MAX_UPLOAD = 1.8 * 1024 * 1024

async function compressImage(file) {
  if (file.size > MAX_SOURCE) {
    throw new Error('This file is larger than 100MB — please choose a smaller photo')
  }
  let bitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    // Browser couldn't decode this format (e.g. HEIC on Chrome/Windows).
    if (file.size > MAX_UPLOAD) {
      throw new Error('This photo format could not be processed in your browser — please save it as JPG or PNG and try again')
    }
    return file // small enough to let the server decide
  }
  // Try progressively smaller output until it fits the server limit.
  for (const [maxDim, quality] of [[1600, 0.85], [1400, 0.75], [1200, 0.65]]) {
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality))
    if (blob && blob.size <= MAX_UPLOAD) {
      // keep the original if it's already a small web-ready file
      if (file.size <= blob.size && file.size <= MAX_UPLOAD && /^image\/(jpeg|png|webp)$/.test(file.type)) return file
      return new File([blob], 'photo.jpg', { type: 'image/jpeg' })
    }
  }
  throw new Error('Could not compress this photo enough — please try a different image')
}

const RECEIPT_MAX_BYTES = 900_000 // stays under the server's 950KB guard and D1's row limit

// Prepare a transfer slip for upload: PDFs pass through, photos/screenshots get
// shrunk to a small JPEG so it uploads fast on a phone.
export async function prepareReceipt(file) {
  if (file.type === 'application/pdf') {
    if (file.size > RECEIPT_MAX_BYTES) throw new Error('That PDF is too big — a screenshot of the receipt works best.')
    return file
  }
  if (!file.type.startsWith('image/')) throw new Error('Please choose a photo, screenshot, or PDF.')
  try {
    const bitmap = await createImageBitmap(file)
    const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close?.()
    for (const quality of [0.82, 0.65, 0.5]) {
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality))
      if (blob && blob.size <= RECEIPT_MAX_BYTES) return new File([blob], 'receipt.jpg', { type: 'image/jpeg' })
    }
    throw new Error('too-large')
  } catch {
    // Browser couldn't decode this format (e.g. HEIC from an iPhone) — send it as-is if small.
    if (file.size <= RECEIPT_MAX_BYTES && ['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) return file
    throw new Error(`We couldn't read that image — please attach a screenshot of the receipt instead.`)
  }
}

// Staff log in with email + password and get a session token; the old shared key is
// still accepted as a master key (bootstrap / lock-out recovery), hence two header shapes.
export const adminLogin = (email, password) =>
  request('/api/admin/login', { method: 'POST', body: { email, password } })

export function adminApi(auth) {
  const { key = '', token = '' } = typeof auth === 'string' ? { key: auth } : (auth || {})
  const headers = token ? { 'x-admin-token': token } : { 'x-admin-key': key }
  return {
    login: () => request('/api/admin/ping', { headers }),
    logout: () => request('/api/admin/logout', { method: 'POST', headers }),
    users: () => request('/api/admin/users', { headers }),
    saveUser: (user) => request('/api/admin/users', { method: 'POST', body: user, headers }),
    setUserPassword: (id, password, current) =>
      request(`/api/admin/users/${encodeURIComponent(id)}/password`, { method: 'POST', body: { password, current }, headers }),
    deleteUser: (id) => request(`/api/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE', headers }),
    assignOrder: (id, adminId) =>
      request(`/api/admin/orders/${id}/assign`, { method: 'POST', body: { admin_id: adminId }, headers }),
    orderLog: (id) => request(`/api/admin/orders/${id}/log`, { headers }),
    products: () => request('/api/admin/products', { headers }),
    saveProduct: (p) => request('/api/admin/products', { method: 'POST', body: p, headers }),
    deleteProduct: (sku) => request(`/api/admin/products/${encodeURIComponent(sku)}`, { method: 'DELETE', headers }),
    reorderProducts: (order) => request('/api/admin/products/reorder', { method: 'POST', body: { order }, headers }),
    saveVariations: (sku, variations) =>
      request(`/api/admin/products/${encodeURIComponent(sku)}/variations`, { method: 'POST', body: { variations }, headers }),
    orders: () => request('/api/admin/orders', { headers }),
    // Slips need the admin key, so fetch as a blob and hand back an object URL.
    orderReceipt: async (id) => {
      const res = await fetch(`/api/admin/orders/${id}/receipt`, { headers })
      if (!res.ok) throw new Error('Receipt not available')
      const blob = await res.blob()
      return { url: URL.createObjectURL(blob), type: blob.type }
    },
    setOrderStatus: (id, status) =>
      request(`/api/admin/orders/${id}/status`, { method: 'POST', body: { status }, headers }),
    resendInvoice: (id) => request(`/api/admin/orders/${id}/invoice`, { method: 'POST', headers }),
    settings: () => request('/api/admin/settings', { headers }),
    testEmail: (to) => request('/api/admin/test-email', { method: 'POST', body: { to }, headers }),
    saveSettings: (values) => request('/api/admin/settings', { method: 'POST', body: values, headers }),
    upload: async (file) => {
      const prepared = await compressImage(file)
      const buf = await prepared.arrayBuffer()
      const res = await fetch('/api/admin/upload', {
        method: 'POST',
        headers: { ...headers, 'content-type': prepared.type || 'application/octet-stream' },
        body: buf,
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Upload failed')
      return data // { url }
    },
  }
}
